'use client'

import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { MutationResult } from '@/components/chat/tool-renderers/mutation-result'
import { PartResultList } from '@/components/chat/tool-renderers/part-results'
import { cn } from '@/lib/utils'

const Q1 = 'What filter does the furnace take?'
const A1 = "Your Daikin furnace takes a 14x24x1 MERV 8 filter. It was due Sep 23, so it's 6 days late. You have one left in Parts."
const Q2 = 'Mark it done.'
const A2 = 'Done. The next filter change is on your calendar for December.'

const FURNACE_FILTER = {
  id: 'furnace-filter',
  name: 'Furnace filter',
  quantity: 1,
  reorderLevel: 2,
  size: '14x24x1',
  category: 'Filters',
  supplier: 'Amazon',
}

const TYPE_MS = 45 // per character, for the homeowner's questions
const STREAM_MS = 55 // per word, for Clark's answers
const TICK_MS = 40

const words = (text: string) => text.split(' ')

// One timeline for the whole exchange, in ms from the moment it scrolls into view.
const T = (() => {
  const q1 = 0
  const card1 = q1 + Q1.length * TYPE_MS + 500
  const a1 = card1 + 350
  const q2 = a1 + words(A1).length * STREAM_MS + 1100
  const card2 = q2 + Q2.length * TYPE_MS + 500
  const a2 = card2 + 350
  const end = a2 + words(A2).length * STREAM_MS
  return { q1, card1, a1, q2, card2, a2, end }
})()

/**
 * Text that reveals over time without reflowing the page: the unrevealed tail is
 * rendered invisibly, so every line's final height is reserved from the start.
 */
function Reveal({ text, shown }: { text: string; shown: number }) {
  return (
    <>
      {text.slice(0, shown)}
      <span className="opacity-0">{text.slice(shown)}</span>
    </>
  )
}

const typed = (text: string, start: number, t: number) =>
  Math.max(0, Math.min(text.length, Math.floor((t - start) / TYPE_MS)))

const streamed = (text: string, start: number, t: number) => {
  const n = Math.max(0, Math.floor((t - start) / STREAM_MS))
  return words(text).slice(0, n).join(' ').length
}

function Appear({ at, t, children }: { at: number; t: number; children: React.ReactNode }) {
  const visible = t >= at
  return (
    <div
      className={cn(
        'transition-[opacity,translate] duration-300 ease-out',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      )}
    >
      {children}
    </div>
  )
}

/**
 * A scripted Clark exchange built from the chat's real result components, so it
 * matches the app: the question is set as a serif heading, result cards sit
 * above the answer, and the answer is plain prose with no bubble.
 */
export function ClarkDemo() {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.5 })
  const reduceMotion = useReducedMotion()
  const [t, setT] = useState(0)

  useEffect(() => {
    if (!inView) return
    if (reduceMotion) {
      setT(T.end)
      return
    }
    const started = performance.now()
    const id = window.setInterval(() => {
      const elapsed = performance.now() - started
      setT(elapsed)
      if (elapsed >= T.end) window.clearInterval(id)
    }, TICK_MS)
    return () => window.clearInterval(id)
  }, [inView, reduceMotion])

  return (
    <div ref={ref} className="rounded-2xl border border-border bg-background p-6 md:p-10">
      {/* Screen readers get the whole exchange at once; the animation is decorative. */}
      <p className="sr-only">
        Example conversation. You ask: {Q1} Clark shows the furnace filter part, low on stock with 1 left, and
        answers: {A1} You say: {Q2} Clark marks Replace furnace filter as done and answers: {A2}
      </p>

      <div aria-hidden inert className="space-y-6">
        <div className="space-y-4">
          <p className="font-serif text-xl leading-snug text-foreground sm:text-2xl">
            <Reveal text={Q1} shown={typed(Q1, T.q1, t)} />
          </p>
          <Appear at={T.card1} t={t}>
            <PartResultList parts={[FURNACE_FILTER]} />
          </Appear>
          <p className="leading-relaxed text-foreground">
            <Reveal text={A1} shown={streamed(A1, T.a1, t)} />
          </p>
        </div>

        <div className="space-y-4 border-t border-border pt-6">
          <p className="font-serif text-xl leading-snug text-foreground sm:text-2xl">
            <Reveal text={Q2} shown={typed(Q2, T.q2, t)} />
          </p>
          <Appear at={T.card2} t={t}>
            <MutationResult success kind="complete" message="Marked Replace furnace filter as done" />
          </Appear>
          <p className="leading-relaxed text-foreground">
            <Reveal text={A2} shown={streamed(A2, T.a2, t)} />
          </p>
        </div>
      </div>
    </div>
  )
}
